/**
 * **Duplicated verbatim from `apps/web-bidder/src/components/Turnstile.tsx`** — keep the two in step.
 * Both apps host unauthenticated, mail-sending or money-moving forms that the Worker guards
 * with the same `turnstile` middleware, so the widget has to exist on both sides. It is app
 * configuration (an env-driven site key), not a presentational primitive, so it does not belong
 * in `packages/ui`.
 */
import { useEffect, useRef, useState } from 'react';
import { TURNSTILE_SITE_KEY, turnstileConfigured } from '../lib/turnstile';

/**
 * Cloudflare Turnstile widget (M8). Lazily loads the Turnstile script, renders the widget, and
 * reports the token (or `''` on expiry/error) via `onToken`. Renders nothing when no site key is
 * configured — callers then send the dev placeholder token instead. The script is loaded once
 * and shared across mounts.
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      theme?: 'auto' | 'light' | 'dark';
    },
  ) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;
function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise<void>((resolve, reject) => {
      const s = document.createElement('script');
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Turnstile script failed to load'));
      document.head.appendChild(s);
    }).catch((err) => {
      // Don't cache a rejected promise — a transient CDN/network failure must be retryable on the
      // next mount, otherwise every subsequent render inherits the first failure forever.
      scriptPromise = null;
      throw err;
    });
  }
  return scriptPromise;
}

/**
 * @param onToken   called with the token (or `''` on expiry/error/reset)
 * @param resetKey  a monotonically increasing value; changing it resets the widget so it issues a
 *                  fresh single-use token (call after a rejected submit, since the old token is spent)
 */
export function Turnstile({
  onToken,
  resetKey = 0,
}: {
  onToken: (token: string) => void;
  resetKey?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  // Reset on demand: Turnstile tokens are single-use, so after a rejected register/payment the
  // spent token must be replaced. Skip the initial render (resetKey 0) — the widget renders fresh.
  useEffect(() => {
    if (resetKey === 0 || !widgetId.current || !window.turnstile) return;
    try {
      window.turnstile.reset(widgetId.current);
      onToken('');
    } catch {
      /* widget not ready — ignore */
    }
  }, [resetKey, onToken]);

  const [loadAttempt, setLoadAttempt] = useState(0);
  useEffect(() => {
    if (!turnstileConfigured) return;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    loadTurnstile()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => onToken(token),
          'expired-callback': () => onToken(''),
          'error-callback': () => onToken(''),
          theme: 'auto',
        });
      })
      .catch(() => {
        // Transient script-load failure: `loadTurnstile` already cleared the cached rejection, so
        // schedule a bounded retry **while still mounted** (re-running this effect) — otherwise the
        // widget would only reload on a remount and the user would be stuck unable to submit.
        if (!cancelled && loadAttempt < 3) {
          retryTimer = setTimeout(() => setLoadAttempt((n) => n + 1), 2_000 * (loadAttempt + 1));
        }
      });
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (widgetId.current && window.turnstile?.remove) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* already removed */
        }
        widgetId.current = null;
      }
    };
  }, [onToken, loadAttempt]);

  if (!turnstileConfigured) return null;
  return <div ref={ref} className="my-2" />;
}
